import { type ExternalPartiesDTO, LocalPartyControllerService, OpenAPI } from 'data/openapi';

/** External and migrated-in parties hosted on the participant, with the cursor to read the changes since. */
export type ExternalPartiesScanResult = Required<ExternalPartiesDTO>;

/**
 * Response of the changes endpoint. Not described in the OpenAPI spec yet (the generated method returns void).
 */
export type ExternalPartiesChangesDTO = ExternalPartiesDTO & {
  added?: string[];
  removed?: string[];
};

/** Error of the external parties scan; `status` is set when the server answered with an HTTP error status. */
export class ExternalPartiesScanError extends Error {
  public readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ExternalPartiesScanError';
    this.status = status;
  }
}

/** Rejects incomplete responses, so they can't replace the kept list, cursor or scan time. */
const toScanResult = (dto: ExternalPartiesDTO | undefined): ExternalPartiesScanResult => {
  if (!dto || !Array.isArray(dto.parties) || typeof dto.cursor !== 'string' || typeof dto.scannedAt !== 'string') {
    throw new ExternalPartiesScanError('Unexpected response from the server.');
  }
  return { parties: dto.parties, cursor: dto.cursor, scannedAt: dto.scannedAt };
};

type ServerSentEvent = {
  event: string;
  data: string;
};

/**
 * Parses the complete events of a server-sent event stream buffer. Comment lines (keep-alives) are skipped.
 * `rest` is the trailing incomplete part, to be prepended to the next chunk.
 */
export const parseServerSentEvents = (buffer: string): { events: ServerSentEvent[]; rest: string } => {
  // A trailing \r may be the first half of a \r\n split across chunks: keep it for the next chunk.
  const heldBack = buffer.endsWith('\r') ? '\r' : '';
  const blocks = buffer
    .slice(0, buffer.length - heldBack.length)
    .replace(/\r\n?/g, '\n')
    .split('\n\n');
  const rest = (blocks.pop() ?? '') + heldBack;

  const events = blocks
    .map((block) => {
      const lines = block.split('\n').filter((line) => line && !line.startsWith(':'));
      const field = (name: string) =>
        lines
          .filter((line) => line.startsWith(`${name}:`))
          .map((line) => line.slice(name.length + 1).replace(/^ /, ''));

      return { event: field('event')[0] ?? 'message', data: field('data').join('\n'), hasData: lines.length > 0 };
    })
    .filter(({ hasData }) => hasData)
    .map(({ event, data }) => ({ event, data }));

  return { events, rest };
};

const parseJson = (data: string): Record<string, unknown> | undefined => {
  try {
    return JSON.parse(data);
  } catch {
    return undefined;
  }
};

const getErrorEventMessage = (event: ServerSentEvent) => {
  const message = parseJson(event.data)?.message;
  return typeof message === 'string' && message ? message : 'The external parties scan failed.';
};

/** Reads the message of an error response, sent either as an `error` event or as a JSON body. */
const readErrorResponseMessage = async (response: Response) => {
  const text = await response.text().catch(() => '');
  const errorEvent = parseServerSentEvents(`${text}\n\n`).events.find(({ event }) => event === 'error');
  if (errorEvent) return getErrorEventMessage(errorEvent);

  const message = parseJson(text)?.message;
  return typeof message === 'string' && message ? message : response.statusText || `HTTP ${response.status}`;
};

const readScanResult = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  decoder: TextDecoder,
  buffer = '',
): Promise<ExternalPartiesScanResult> => {
  const { done, value } = await reader.read();
  const { events, rest } = parseServerSentEvents(
    buffer + decoder.decode(value, { stream: !done }) + (done ? '\n\n' : ''),
  );

  const event = events.find((e) => e.event === 'result' || e.event === 'error');
  if (event?.event === 'result') {
    return toScanResult(parseJson(event.data) as ExternalPartiesDTO | undefined);
  }
  if (event?.event === 'error') {
    throw new ExternalPartiesScanError(getErrorEventMessage(event));
  }
  if (done) {
    throw new ExternalPartiesScanError('The external parties scan ended without a result.');
  }
  return readScanResult(reader, decoder, rest);
};

/**
 * GET /participants/{name}/parties/local/external – full scan for external and migrated-in parties.
 * Slow on large networks (a few minutes). The response is a server-sent event stream: keep-alive comments while
 * the scan runs, then one `result` or `error` event. Read with fetch, as EventSource can't send the Authorization header.
 */
export const scanExternalParties = async (participantName: string): Promise<ExternalPartiesScanResult> => {
  const token = typeof OpenAPI.TOKEN === 'string' ? OpenAPI.TOKEN : undefined;
  const response = await fetch(
    `${OpenAPI.BASE}/participants/${encodeURIComponent(participantName)}/parties/local/external`,
    {
      headers: {
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );

  if (!response.ok) {
    throw new ExternalPartiesScanError(await readErrorResponseMessage(response), response.status);
  }
  if (!response.body) {
    throw new ExternalPartiesScanError('The external parties scan returned no data.');
  }

  const reader = response.body.getReader();
  try {
    return await readScanResult(reader, new TextDecoder());
  } finally {
    reader.cancel().catch(() => undefined);
  }
};

/**
 * POST /participants/{name}/parties/local/external/changes – the external parties, read as the changes since `cursor`.
 * Answers 409 with a `reason` when the changes can't be read from the cursor and a full scan is needed.
 */
export const getExternalPartyChanges = async (
  participantName: string,
  cursor: string,
): Promise<ExternalPartiesScanResult> => {
  const response = (await LocalPartyControllerService.getExternalPartyChanges(participantName, {
    cursor,
  })) as unknown as ExternalPartiesChangesDTO;
  return toScanResult(response);
};
